import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, get, onValue, off, update, push } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

function ReportContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportMessages, setReportMessages] = useState({});
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);


  useEffect(() => {
    const auth = getAuth();
    const db = getDatabase();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.log("No user logged in → redirect to login");
        navigate("/login", { replace: true });
        return;
      }

      try {
        const userRef = ref(db, `USERS/${user.uid}`);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
          // console.log("User not found in database → redirect to home");
          navigate("/", { replace: true });
          return;
        }

        const userData = snapshot.val();
        // console.log("Logged in user:", user.uid, userData);

        if (userData.USERISADMIN !== "Y") {
          console.log("User is not admin → redirect to home");
          navigate("/", { replace: true });
          return;
        }

        // ✅ Admin verified
        setIsAdmin(true);
        setLoading(false);
        // console.log("Admin verified, can load reports");

      } catch (error) {
        // console.error("Error verifying admin:", error);
        navigate("/login", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);


  useEffect(() => {
    if (!isAdmin) return;

    const db = getDatabase();
    const reportsRef = ref(db, "REPORTS"); // Make sure this matches your DB node

    // console.log("Starting reports listener...");

    onValue(
      reportsRef,
      (snapshot) => {
        // console.log("Listener fired!");
        const data = snapshot.val();
        // console.log("Snapshot data:", data);

        if (!data) {
          setReports([]);
          // console.log("No reports found");
          return;
        }

        const formattedReports = Object.entries(data).map(([id, report]) => ({
          id,
          ...report,
        }));

        setReports(formattedReports);
        // console.log("Loaded reports:", formattedReports);
      },
      (error) => {
        // console.error("Listener error:", error);
      }
    );

    // Cleanup listener on unmount
    return () => off(reportsRef, "value");
  }, [isAdmin]);

  const fetchReportMessages = async (reportId) => {
    try {
      const database = getDatabase();
      const messagesRef = ref(database, `REPORTS/${reportId}/MESSAGES`);
      
      const snapshot = await get(messagesRef);
      if (snapshot.exists()) {
        const messagesData = snapshot.val();
        const messagesArray = Object.entries(messagesData).map(([id, msg]) => ({
          id,
          ...msg
        })).sort((a, b) => a.createdAt - b.createdAt);
        
        setReportMessages(prev => ({
          ...prev,
          [reportId]: messagesArray
        }));
      } else {
        setReportMessages(prev => ({
          ...prev,
          [reportId]: []
        }));
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      setReportMessages(prev => ({
        ...prev,
        [reportId]: []
      }));
    }
  };

  const handleSendMessage = async (reportId) => {
    if (!messageText.trim()) return;

    setSendingMessage(true);
    try {
      const database = getDatabase();
      const messageRef = ref(database, `REPORTS/${reportId}/MESSAGES`);
      
      const now = new Date();
      const newMessage = {
        MESSAGE_TEXT: messageText,
        SENT_BY: "ADMIN",
        SENT_DATE: now.toISOString(),
        createdAt: Date.now()
      };

      await push(messageRef, newMessage);
      setMessageText("");
      
      // Refresh messages
      await fetchReportMessages(reportId);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleToggleReportStatus = async (reportId, currentStatus) => {
    try {
      const database = getDatabase();
      const reportRef = ref(database, `REPORTS/${reportId}`);
      
      const newStatus = currentStatus === "Y" ? "N" : "Y";
      await update(reportRef, {
        REPORT_ISOPEN: newStatus
      });

      // Refresh the reports list
      const reportsRef = ref(database, "REPORTS");
      const snapshot = await get(reportsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const formattedReports = Object.entries(data).map(([id, report]) => ({
          id,
          ...report,
        }));

        setReports(formattedReports);

        // Update selected report if it's the one we just updated
        if (selectedReport?.id === reportId) {
          setSelectedReport(prev => ({
            ...prev,
            REPORT_ISOPEN: newStatus
          }));
        }
      }
    } catch (error) {
      console.error("Failed to toggle report status:", error);
    }
  };


  if (loading) {
    return <>Checking admin permissions...</>;
  }

  return (
    <div>
      <h1 className="mb-4">Admin Reports</h1>

      <div className="row g-3">
        <div className="col-12">
          <div className="mb-4">
            <p className="text-muted">Manage event reports from users</p>
          </div>

          {reports.length === 0 ? (
            <div className="card p-3">
              <p className="mb-0 text-gray-500">No reports found.</p>
            </div>
          ) : (
            <div className="reports-list">
              {reports.map((report) => {
                const isExpanded = selectedReport?.id === report.id;
                return (
                  <div key={report.id} className="card mb-3 border-0 shadow-sm">
                    <div className="card-body">
                      {/* Report Header */}
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <h6 className="card-title mb-2 fw-bold">
                            {report.REPORT_EVENTNAME}
                          </h6>
                          <p className="mb-2 text-muted small">
                            #{report.id} <span className="event-badges">{report.REPORT_EVENTREASON}</span>
                          </p>
                          <p className="text-gray-500 mb-0 small">
                            <strong>Description:</strong> {report.REPORT_DESCRIPTION}
                          </p>
                        </div>

                        <div className="col-md-4 text-md-end text-sm-start pt-3 pt-md-0">
                          <div className="d-flex justify-content-md-end justify-content-sm-start align-items-center gap-2">
                            <span className={`event-badges ${report.REPORT_ISOPEN === "Y" ? "open" : "resolved"}`}>
                              {report.REPORT_ISOPEN === "Y" ? <><i className="bi bi-check2-circle me-1 admin-badge"></i>Open</> : <><i className="bi bi-check2-circle me-1 verified-badge"></i>Resolved</>}
                            </span>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                if (isExpanded) {
                                  setSelectedReport(null);
                                } else {
                                  setSelectedReport(report);
                                  fetchReportMessages(report.id);
                                }
                              }}
                            >
                              {isExpanded ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Chat Section - Collapsible */}
                      {isExpanded && (
                        <div className="mt-4 pt-3 border-top">
                          <div className="mb-3">
                            <p className="mb-2">
                              <strong>Event:</strong> <Link to={`/event/${report.REPORT_EVENTURL}`}>{report.REPORT_EVENTNAME}</Link>
                            </p>
                            <p className="mb-2">
                              <strong>Reason:</strong> {report.REPORT_EVENTREASON}
                            </p>
                          </div>

                          {/* Messages Container */}
                          <div className="messages-container mb-3 p-3" style={{ backgroundColor: "var(--chat-bg)", borderRadius: "8px", maxHeight: "300px", overflowY: "auto", minHeight: "150px", border: "var(--chat-border)" }}>
                            {!reportMessages[report.id] || reportMessages[report.id].length === 0 ? (
                              <p className="text-center text-muted mb-0 small">No messages yet. Start the conversation!</p>
                            ) : (
                              reportMessages[report.id].map((msg, idx) => {
                                const msgDate = msg.SENT_DATE ? new Date(msg.SENT_DATE) : null;
                                const formattedDateTime = msgDate ? `${msgDate.toLocaleDateString()} ${msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Unknown';
                                return (
                                  <div key={idx} className={`mb-2 ${msg.SENT_BY === "ADMIN" ? "text-end" : "text-start"}`}>
                                    <div style={{
                                      display: "inline-block",
                                      backgroundColor: msg.SENT_BY === "ADMIN" ? "var(--chat-message-admin-bg)" : "inherit",
                                      color: "var(--chat-message-text)",
                                      padding: "10px 14px",
                                      borderRadius: "12px",
                                      maxWidth: "75%",
                                      wordWrap: "break-word"
                                    }}>
                                      <p className="mb-1 small">{msg.MESSAGE_TEXT}</p>
                                      <small style={{ opacity: 0.7 }}>
                                        {msg.SENT_BY === "ADMIN" ? "Admin" : "User"} • {formattedDateTime}
                                      </small>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Message Input - Only if report is open */}
                          {report.REPORT_ISOPEN === "Y" ? (
                            <div className="row g-2 mb-2">
                              <div className="col">
                                <textarea
                                  className="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Type your response..."
                                  value={messageText}
                                  onChange={(e) => setMessageText(e.target.value)}
                                  disabled={sendingMessage}
                                  style={{ borderRadius: "8px" }}
                                />
                              </div>
                            </div>
                          ) : null}

                          <div className="row g-2">
                            <div className="col">
                              {report.REPORT_ISOPEN === "Y" && (
                                <button
                                  className="btn btn-sm btn-primary w-100"
                                  onClick={() => handleSendMessage(report.id)}
                                  disabled={sendingMessage || !messageText.trim()}
                                  style={{ borderRadius: "8px" }}
                                >
                                  <i className="bi bi-send me-1"></i>
                                  {sendingMessage ? "Sending..." : "Send Response"}
                                </button>
                              )}
                            </div>
                            <div className="col">
                              <div className="form-check form-switch pt-1">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`reportSwitch${report.id}`}
                                  checked={report.REPORT_ISOPEN === "Y"}
                                  onChange={() =>
                                    handleToggleReportStatus(
                                      report.id,
                                      report.REPORT_ISOPEN
                                    )
                                  }
                                />
                                <label className="form-check-label" htmlFor={`reportSwitch${report.id}`}>
                                  {report.REPORT_ISOPEN === "Y" ? "Report Open" : "Report Closed"}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportContent;
